import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Layout,
  message,
  Modal,
  Row,
  Select,
} from "antd";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ConfirmAction from "../../Components/ConfirmAction/ConfirmAction";
import {
  formatNumber,
  formatDate,
  formatString,
  handleExportToExcel,
  handleExportToPDF,
  getUserRole
} from "../../Utils/Common";
import { hasRole } from "../../Utils/AccessControl";
import CustomTable from "../../Components/Table/CustomTable";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useTableData } from "../../Utils/useTableData";

const { Option } = Select;

const AdvancePayment = () => {
  const [loading,        setLoading]        = useState("");
  const [showModal,      setShowModal]      = useState("");
  const [form]                              = Form.useForm();
  const [selectedRow,    setSelectedRow]    = useState<any>({});
  const [companies,      setCompanies]      = useState<any[]>([]);

  const {
    dataSource,
    dataSize,
    loading: tableLoading,
    filterConfig,
    setFilterConfig,
    refetch: fetchTableData,
    handleChangePage,
    handlePageSizeChange,
    fetchExport,
  } = useTableData<any>({ endpoint: "advance_payments/get", isFormData: true });

  const typeOptions = [
    { value: "cash",   label: "Cash"   },
    { value: "check",  label: "Check"  },
    { value: "online", label: "Online" },
  ];

  function clearFields() {
    setSelectedRow({});
    form.resetFields();
  }

  function handleFilterChange(value: any, key: string) {
    setFilterConfig((prev: any) => ({ ...prev, [key]: value, page: 1 }));
  }

  const columns = [
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
    },
    {
      title: "Owner",
      dataIndex: "company_owner_name",
      key: "company_owner_name",
    },
    {
      title: "Amount Paid",
      dataIndex: "amount_paid",
      key: "amount_paid",
      align: "right" as any,
      render: (value: number) => <span>{formatNumber(value)}</span>,
    },
    {
      title: "Remaining Amount",
      dataIndex: "available_amount",
      key: "available_amount",
      align: "right" as any,
      render: (value: number) => <span>{formatNumber(value)}</span>,
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (text: string) => (
        <span className="capitalize">{text ?? "—"}</span>
      ),
    },
    {
      title: "Added By",
      dataIndex: "added_by_name",
      key: "added_by_name",
    },
    {
      title: "Date",
      dataIndex: "added_on",
      key: "added_on",
      render: (date: any) => formatDate(date, "MMM DD, YYYY h:mm A"),
    },
  ];

  async function exportToExcel() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) {
        handleExportToExcel(data, columns, "AdvancePayment");
      } else {
        message.error("Failed to fetch data for export.");
      }
    } catch {
      message.error("Error exporting data.");
    } finally {
      setLoading("");
    }
  }

  async function exportToPDF() {
    setLoading("export");
    try {
      const { success, data } = await fetchExport();
      if (success) {
        handleExportToPDF(
          data,
          columns,
          "AdvancePayment.pdf",
          {
            title: "List of Advance Payments",
            content: `Generated on ${new Date().toLocaleString()}`,
          },
          `Total Records: ${data.length}`
        );
      } else {
        message.error("Failed to fetch data for PDF export.");
      }
    } catch {
      message.error("Error exporting PDF.");
    } finally {
      setLoading("");
    }
  }

  async function submitForm(data: any) {
    setLoading("submit");
    const response = await postRequest(
      `advance_payments/${showModal}`,
      { advance_payment_id: selectedRow.id, ...data },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setShowModal("");
        setLoading("");
        fetchTableData();
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${showModal} advance payment.`
      );
    }
  }

  async function deleteAdvancePayment() {
    const response = await postRequest(
      `advance_payments/delete`,
      { advance_payment_id: selectedRow.id },
      true
    );
    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        setShowModal("");
        clearFields();
        fetchTableData();
      }, 1000);
    } else {
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to delete advance payment."
      );
    }
  }

  async function fetchCompanies() {
    setCompanies([]);
    const response = await getRequest(`companies/get`, {}, true);
    if (response.success) {
      setCompanies(response.data.data);
    }
  }

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title="Advance Payment"
              withDateRangeFilter={false}
              withAddButton={hasRole(getUserRole(), "cashier")}
              addBtnText="Advance Payment"
              onCreate={() => setShowModal("create")}
              withExportDropdownButton={hasRole(getUserRole(), "cashier")}
              disableExportButton={dataSource?.length === 0}
              onExportToExcel={() => exportToExcel()}
              onExportToPDF={() => exportToPDF()}
              withSearchBtn={hasRole(getUserRole(), "cashier")}
              onSearch={(value: any) =>
                setFilterConfig((prev: any) => ({ ...prev, search: value }))
              }
            />
          </Col>

          {/* ── Filters ── */}
          {hasRole(getUserRole(), "cashier") && (
            <>
              <Col xs={24} className="mb-1">
                <span><strong>Filter By:</strong></span>
              </Col>
              <Col span={24} className="mb-3">
                <Row gutter={[10, 10]}>
                  <Col xs={24} md={12} lg={5}>
                    <Select
                      className="w-full"
                      showSearch
                      placeholder="Select company"
                      allowClear
                      onChange={(value) => handleFilterChange(value, "company_id")}
                      filterOption={(input: string, option?: any) =>
                        option?.children?.toLowerCase().includes(input.toLowerCase())
                      }
                    >
                      <Option value="">All Companies</Option>
                      {companies.map((company: any) => (
                        <Option key={company.id} value={company.id}>
                          {company.name}
                        </Option>
                      ))}
                    </Select>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <Select
                      className="w-full"
                      placeholder="Filter by type"
                      allowClear
                      onChange={(value) => handleFilterChange(value, "type")}
                      options={typeOptions}
                    />
                  </Col>
                </Row>
              </Col>
            </>
          )}

          <Col span={24}>
            <div className="table-container overflow-auto">
              <div className="min-w-[600px]">
                <CustomTable
                  data={dataSource}
                  columns={columns}
                  rowKey="id"
                  loading={tableLoading}
                  size="small"
                  pagination={{
                    current: filterConfig.page,
                    total: dataSize,
                    showSizeChanger: true,
                    onChange: (page: number) => handleChangePage(page),
                    onShowSizeChange: (_: number, size: number) => handlePageSizeChange(size),
                  }}
                  handleRowClick={(record: any) => {
                    setShowModal("update");
                    setSelectedRow(record);
                    form.setFieldsValue({ ...record });
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Layout>

      {/* ── Create / Update Modal ── */}
      <Modal
        open={showModal === "create" || showModal === "update"}
        title={`${showModal === "create" ? "Add" : "Edit"} Advance Payment`}
        centered
        footer={null}
        onCancel={() => {
          clearFields();
          setShowModal("");
        }}
        width={600}
        maskClosable={false}
      >
        <div className="max-h-[70vh] overflow-y-auto pr-2">
          <Form form={form} onFinish={submitForm} layout="vertical">
            <Row gutter={[10, 10]} className="mt-2">

              {/* Company */}
              <Col xs={24} md={24}>
                <Form.Item
                  label="Company"
                  name="company_id"
                  rules={[{ required: true, message: "Company is required." }]}
                >
                  <Select
                    placeholder="Select company"
                    showSearch
                    allowClear
                    options={companies.map((c: any) => ({ value: c.id, label: c.name }))}
                    filterOption={(input, option) =>
                      typeof option?.label === "string" &&
                      option.label.toLowerCase().includes(input.toLowerCase())
                    }
                  />
                </Form.Item>
              </Col>

              {/* Amount */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Amount"
                  name="amount_paid"
                  rules={[{ required: true, message: "Amount paid is required." }]}
                >
                  <InputNumber
                    style={{ width: "100%" }}
                    min={0}
                    precision={2}
                    placeholder="0.00"
                    formatter={(value) =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                    }
                    parser={(value) => value?.replace(/,/g, "") as any}
                  />
                </Form.Item>
              </Col>

              {/* Type */}
              <Col xs={24} md={12}>
                <Form.Item
                  label="Type"
                  name="type"
                  rules={[{ required: true, message: "Type is required." }]}
                >
                  <Select
                    placeholder="Select type"
                    allowClear
                    options={typeOptions}
                  />
                </Form.Item>
              </Col>

              {/* Actions */}
              <Col xs={24} className="flex justify-end gap-3 mt-2">
                <Button
                  onClick={() => {
                    clearFields();
                    setShowModal("");
                  }}
                >
                  Cancel
                </Button>
                {showModal === "update" && (
                  <ConfirmAction
                    title="Are you sure you want to delete this record?"
                    onConfirm={() => deleteAdvancePayment()}
                  >
                    <Button danger>Delete</Button>
                  </ConfirmAction>
                )}
                <Button
                  htmlType="submit"
                  type="primary"
                  loading={loading === "submit"}
                >
                  Submit
                </Button>
              </Col>

            </Row>
          </Form>
        </div>
      </Modal>

      <PersistentFooter />
    </Layout>
  );
};

export default AdvancePayment;